import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, X, Search, Pencil, UserX, UserCheck, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';
import { NAVY, AMBER, BORDER_COLOR, BG_FIELD, bubbleStyle, navyBtn, fieldStyle } from '../../styles/tokens';
import BulkUploadModal from '../../components/shared/BulkUploadModal';
import { usePaginatedSort } from '../../shared/hooks/usePaginatedSort';
import PaginationBar from '../../shared/components/PaginationBar';
import SortableHeader from '../../shared/components/SortableHeader';

// Company role (job title) options. The stored `personas.company_role` value IS the key here
// (free varchar; the badge/dropdown match on it directly), so key === the label string. Order:
// "— Sin rol —" first, then the 10 titles alphabetically. Legacy lowercase "ingeniero" (1 record)
// consolidates into "Ingeniero" — it renders via the raw-value fallback until re-selected by hand.
const COMPANY_ROLES = [
  { key: '', label: '— Sin rol —' },
  { key: 'Asalariado', label: 'Asalariado' },
  { key: 'Field', label: 'Field' },
  { key: 'Ingeniero', label: 'Ingeniero' },
  { key: 'Oficina', label: 'Oficina' },
  { key: 'Oficina Proyectos', label: 'Oficina Proyectos' },
  { key: 'Project Engineer', label: 'Project Engineer' },
  { key: 'Project Manager', label: 'Project Manager' },
  { key: 'Safety', label: 'Safety' },
  { key: 'Serv Prof', label: 'Serv Prof' },
  { key: 'Socios', label: 'Socios' },
];

const COMPANY_ROLE_COLORS = {
  'Asalariado':        { bg: '#ccfbf1', color: '#0f766e' },
  'Field':             { bg: '#dcfce7', color: '#166534' },
  'Ingeniero':         { bg: '#dbeafe', color: '#1e40af' },
  'Oficina':           { bg: '#e0e7ff', color: '#1e3a5f' },
  'Oficina Proyectos': { bg: '#ede9fe', color: '#6b21a8' },
  'Project Engineer':  { bg: '#cffafe', color: '#155e75' },
  'Project Manager':   { bg: '#fef3c7', color: '#92400e' },
  'Safety':            { bg: '#fee2e2', color: '#991b1b' },
  'Serv Prof':         { bg: '#fce7f3', color: '#9d174d' },
  'Socios':            { bg: '#f3e8ff', color: '#7e22ce' },
};

/**
 * Clasificación de PTO — `personas.pto_classification` es UN eje con CUATRO estados.
 * V56: reemplaza el booleano `is_exempt` de V55, que no podía expresar 'no_accrual'.
 *
 * El motor de PTO decide con esta columna, así que el valor no es cosmético:
 *   — sin clasificar —  NO ACUMULA. El cierre la saltea (gate 2c, razón 'unclassified')
 *                       hasta que se le asigne una clasificación. Es el estado honesto de
 *                       un contratado nuevo, y hay que poder encontrarlo.
 *   Exento              acumula, vacaciones sin tope, enfermedad 120 h.
 *   No Exento           acumula, vacaciones 192 h, enfermedad 96 h.
 *   Sin acumulación     FUERA del régimen (subcontratados): tienen horas en Nómina
 *                       por factura y aun así no acumulan nada.
 *
 * ⚠ CAMBIÓ EN V57 — antes decía acá y en el texto de ayuda que "sin clasificar se trata
 * como No Exento". Eso era V56. V57 lo invirtió: sin clasificar ya NO acumula, se saltea y
 * se reporta. El motivo está medido en el gate 2c del servicio (cerrarla como No Exento y
 * reclasificarla después a 'no_accrual' deja acumulación que el motor no limpia sola).
 *
 * ⚠ Los valores son los del CHECK del esquema. Cambiarlos acá sin cambiar el CHECK
 * (y ptoClassification en personaController) hace que el guardado falle o caiga a NULL.
 */
const PTO_CLASS_OPTIONS = [
  { value: '',           label: '— sin clasificar —' },
  { value: 'exempt',     label: 'Exento' },
  { value: 'non_exempt', label: 'No Exento' },
  { value: 'no_accrual', label: 'Sin acumulación' },
];

const PTO_CLASS_VALUES = new Set(['exempt', 'non_exempt', 'no_accrual']);

/**
 * ⚠⚠ Un `<select>` solo maneja strings, así que el NULL necesita traducirse en los dos
 * sentidos. Estas dos funciones son INVERSAS EXACTAS — `toSelect(fromSelect(s)) === s`
 * y `fromSelect(toSelect(v)) === v` para los CUATRO valores — y de eso depende que
 * abrir una persona y guardarla sin tocar el select devuelva el MISMO valor.
 *
 * Por qué importa tanto: `handleSave` manda el payload COMPLETO en cada guardado, sin
 * diffear. Si la hidratación colapsara el NULL, abrir a alguien para corregirle una
 * tilde en el nombre y dar Guardar le afirmaría un régimen que nadie decidió. Con 154
 * sin clasificar eso se erosiona solo y sin rastro.
 *
 * `fromSelect` filtra contra PTO_CLASS_VALUES en vez de devolver el string crudo: si
 * el select trajera algo fuera del CHECK, va a NULL (lado seguro) y no a una violación
 * de constraint ni a un régimen inventado.
 */
const ptoClassToSelect   = v => (PTO_CLASS_VALUES.has(v) ? v : '');
const ptoClassFromSelect = s => (PTO_CLASS_VALUES.has(s) ? s : null);

/** Etiqueta y color en la tabla. Los CUATRO estados se ven distinto. */
const PTO_CLASS_DISPLAY = {
  exempt:     { label: 'Exento',          bg: '#ede9fe', color: '#6b21a8' },
  non_exempt: { label: 'No Exento',       bg: '#e0e7ff', color: '#1e3a5f' },
  no_accrual: { label: 'Sin acumulación', bg: '#f1f5f9', color: '#475569' },
  null:       { label: 'Sin clasificar',  bg: '#fef3c7', color: '#92400e' },
};

/**
 * Orden de la columna PTO — primer consumidor de `customCompare` del hook.
 *
 * ⚠ HACE FALTA: `usePaginatedSort` manda los NULL SIEMPRE AL FINAL, en las DOS
 * direcciones (por diseño, para columnas donde el vacío es ausencia de dato). Acá el
 * NULL no es ausencia: es "sin clasificar", y encontrar a esa gente es EL caso de uso
 * de la columna.
 *
 * MEDIDO en V55: hoy no se nota, porque los sin clasificar son la mayoría y caen en la
 * primera página de todas formas. Se vuelve el problema JUSTO DESPUÉS de la carga de
 * clasificación, cuando sean unos pocos —los contratados nuevos, los que hay que
 * encontrar— y "nulls al final" los deje en la última página en las dos direcciones.
 * O sea rompe exactamente cuando la columna empieza a servir.
 *
 * Enum de CUATRO con el NULL de primera clase (V56): `asc` trae los sin clasificar al
 * frente. Constante de módulo (referencia estable) porque el hook la lleva en las deps
 * de su useMemo.
 */
const PTO_CLASS_SORT_RANK = { null: 0, exempt: 1, non_exempt: 2, no_accrual: 3 };
function ptoClassCompare(a, b, key, dir) {
  if (key !== 'pto_classification') return undefined;   // el resto lo ordena el hook
  const rank = v => PTO_CLASS_SORT_RANK[PTO_CLASS_VALUES.has(v) ? v : 'null'];
  const cmp = rank(a?.pto_classification) - rank(b?.pto_classification);
  return dir === 'asc' ? cmp : -cmp;
}

/**
 * Una columna DATE llega del backend como ISO (`2026-06-15T00:00:00.000Z`) y un
 * `<input type="date">` necesita `YYYY-MM-DD`. Se RECORTA el string, no se construye
 * un Date: convertir zonas es lo que corría el día en V46. Sobre una columna DATE
 * (medianoche UTC) el recorte devuelve el día correcto.
 */
const dateToInput = v => (v ? String(v).slice(0, 10) : '');

export default function PersonasPage() {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ external_id: '', name: '', company_role: '', wioa: false,
                                     pto_classification: '', hire_date: '', hourly_rate: '',
                                     pto_socio_user_id: '' });
  const [saving, setSaving] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  // V69 — los socios CON CUENTA, para el selector del aprobador administrativo.
  // Se piden una sola vez: es el mismo endpoint que alimenta el slot de socio de la
  // ganga, y resuelve contra `user_roles` (usuarios), no contra `personas`.
  const [socios, setSocios] = useState([]);

  function refreshPersonas() {
    client.get('/api/nomina/personas', { params: { active: showInactive ? undefined : 'true' } })
      .then(pRes => setPersonas(pRes.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => { refreshPersonas(); }, [showInactive]);

  useEffect(() => {
    client.get('/api/nomina/users-by-role?role=socio')
      .then(r => setSocios(r.data || []))
      .catch(() => setSocios([]));
  }, []);

  function openCreate() {
    setEditing(null);
    // pto_classification arranca en '' = sin clasificar. NO en 'non_exempt': un
    // contratado nuevo no está clasificado, y afirmar un régimen acá es inventar el dato.
    setForm({ external_id: '', name: '', company_role: '', wioa: false,
              pto_classification: '', hire_date: '', hourly_rate: '',
              // '' = sin socio asignado. Es el estado honesto de un alta: sus ausencias
              // administrativas se bloquean con mensaje hasta que alguien lo defina.
              pto_socio_user_id: '' });
    setModalOpen(true);
  }

  function openEdit(persona) {
    setEditing(persona);
    setForm({
      external_id: persona.external_id || '',
      name: persona.name,
      company_role: persona.company_role || '',
      wioa: !!persona.wioa,
      // ⚠ ptoClassToSelect preserva el NULL como '' (y handleSave lo manda de vuelta
      // como null). Es lo que hace que abrir y guardar sin tocar el select no
      // cambie nada. NUNCA colapsar el NULL: afirmaría un régimen que nadie decidió.
      pto_classification: ptoClassToSelect(persona.pto_classification),
      hire_date: dateToInput(persona.hire_date),
      // NULL = no tiene tarifa (19 de 298). Se hidrata como '' y handleSave lo manda de
      // vuelta como null, para que abrir y guardar sin tocarla no invente un 0.
      hourly_rate: persona.hourly_rate == null ? '' : String(persona.hourly_rate),
      // ⚠ MISMA TRAMPA que hourly_rate e is_exempt: el payload va COMPLETO en cada
      // guardado, así que si esto no se hidrata acá, abrir a alguien para corregirle el
      // NOMBRE le borraría el socio asignado — y sus ausencias administrativas quedarían
      // bloqueadas sin que nadie toque ese campo. El String() es para que el <select>
      // matchee su value (los option value son strings).
      pto_socio_user_id: persona.pto_socio_user_id == null ? '' : String(persona.pto_socio_user_id),
    });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) { alert('El nombre es requerido.'); return; }
    setSaving(true);
    try {
      // ⚠ El payload va COMPLETO en cada guardado (no se diffea), así que cada campo
      // tiene que devolver EXACTAMENTE lo que se hidrató si el usuario no lo tocó.
      // Para pto_classification eso significa mandar `null` explícito cuando está sin
      // clasificar — ni `undefined` (el backend no tocaría la columna, que acá daría
      // el mismo resultado pero por accidente) ni `false` (afirmaría "No Exento").
      const payload = {
        external_id: form.external_id || null,
        name: form.name.trim(),
        company_role: form.company_role || null,
        wioa: !!form.wioa,
        pto_classification: ptoClassFromSelect(form.pto_classification),
        hire_date: form.hire_date || null,
        hourly_rate: form.hourly_rate === '' ? null : form.hourly_rate,
        // null explícito = "sin socio asignado", que es un estado real y hay que poder
        // VOLVER a él (si alguien deja la empresa, su gente queda sin aprobador y el
        // bloqueo tiene que avisar). Number() porque el backend valida entero.
        pto_socio_user_id: form.pto_socio_user_id === '' ? null : Number(form.pto_socio_user_id),
      };

      if (editing) {
        const res = await client.put(`/api/nomina/personas/${editing.id}`, payload);
        setPersonas(prev => prev.map(p => p.id === editing.id ? res.data : p));
      } else {
        const res = await client.post('/api/nomina/personas', payload);
        setPersonas(prev => [...prev, res.data]);
      }
      setModalOpen(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(persona) {
    try {
      const res = await client.put(`/api/nomina/personas/${persona.id}`, { is_active: !persona.is_active });
      setPersonas(prev => prev.map(p => p.id === persona.id ? res.data : p));
    } catch (err) {
      alert(err.response?.data?.error || 'Error.');
    }
  }

  const filtered = personas.filter(p => {
    const term = search.toLowerCase();
    const matchesSearch = p.name.toLowerCase().includes(term) || (p.external_id || '').toLowerCase().includes(term);
    if (!matchesSearch) return false;
    if (!showInactive && !p.is_active) return false;
    return true;
  });

  const {
    pageItems, page, pageSize, totalPages, totalItems,
    sortKey, sortDir, toggleSort, setPage, setPageSize,
  } = usePaginatedSort(filtered, { defaultSortKey: 'name', customCompare: ptoClassCompare });

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 800, margin: '0 auto' }}>
      <button onClick={() => navigate('/horas')} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#64748b', fontSize: 13, marginBottom: 14, fontFamily: 'Inter, sans-serif',
      }}>
        <ArrowLeft size={16} /> Volver
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: NAVY, margin: 0 }}>Gestión de Empleados</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowBulk(true)} style={{
            ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', fontSize: 12, background: '#475569',
          }}>
            <Upload size={14} /> Carga Masiva
          </button>
          <button onClick={openCreate} style={{
            ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', fontSize: 12,
          }}>
            <Plus size={14} /> Nuevo Empleado
          </button>
        </div>
      </div>

      {/* Search + filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre o ID..."
            style={{ ...fieldStyle, paddingLeft: 30 }}
          />
        </div>
        <label style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Mostrar inactivos
        </label>
      </div>

      {/* Table */}
      <div style={bubbleStyle}>
        {/* Table header */}
        {/* ⚠ Este gridTemplateColumns está declarado DOS VECES (header y filas).
            Tocar uno sin el otro desalinea la tabla. */}
        <div style={{
          display: 'grid', gridTemplateColumns: '80px 1fr 140px 110px 80px 100px',
          gap: 8, padding: '0 0 12px', borderBottom: `2px solid ${BORDER_COLOR}`,
          fontSize: 11, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          <SortableHeader label="ID"     sortKey="external_id"  currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Nombre" sortKey="name"         currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Rol"    sortKey="company_role" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          {/* Columna propia y ordenable, NO un badge: un badge ausente no distingue
              NULL de false, y encontrar a los SIN CLASIFICAR es el caso de uso. */}
          <SortableHeader label="PTO"    sortKey="pto_classification" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Estado" sortKey="is_active"    currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <span style={{ textAlign: 'right' }}>Acciones</span>
        </div>

        {/* Rows */}
        {pageItems.map(p => (
          <div key={p.id} style={{
            display: 'grid', gridTemplateColumns: '80px 1fr 140px 110px 80px 100px',
            gap: 8, alignItems: 'center',
            padding: '10px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
            opacity: p.is_active ? 1 : 0.55, fontSize: 13,
            cursor: 'pointer',
          }} onClick={() => openEdit(p)}>
            <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>
              {p.external_id || '—'}
            </span>
            <span style={{ fontWeight: '600', color: NAVY, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {p.name}
              {p.wioa && (
                <span style={{
                  display: 'inline-block', padding: '1px 6px', borderRadius: 20,
                  fontSize: 9, fontWeight: '700', letterSpacing: '0.03em',
                  background: '#dcfce7', color: '#166534',
                }}>
                  WIOA
                </span>
              )}
            </span>
            <span style={{ fontSize: 12 }}>
              {p.company_role ? (
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 20,
                  fontSize: 10, fontWeight: '600',
                  background: (COMPANY_ROLE_COLORS[p.company_role] || {}).bg || '#f1f5f9',
                  color: (COMPANY_ROLE_COLORS[p.company_role] || {}).color || '#475569',
                }}>
                  {(COMPANY_ROLES.find(r => r.key === p.company_role) || {}).label || p.company_role}
                </span>
              ) : '—'}
            </span>
            {/* PTO. Los CUATRO estados se ven distinto — "Sin clasificar" en ámbar es
                visible a propósito: es trabajo pendiente, no un valor válido. */}
            <span style={{ fontSize: 12 }}>
              {(() => {
                const d = PTO_CLASS_DISPLAY[PTO_CLASS_VALUES.has(p.pto_classification) ? p.pto_classification : 'null'];
                return (
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 20,
                    fontSize: 10, fontWeight: '600', background: d.bg, color: d.color,
                  }}>
                    {d.label}
                  </span>
                );
              })()}
            </span>
            <span>
              <span style={{
                display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                fontSize: 11, fontWeight: '600',
                background: p.is_active ? '#f0fdf4' : '#fef2f2',
                color: p.is_active ? '#166534' : '#991b1b',
              }}>
                {p.is_active ? 'Activo' : 'Inactivo'}
              </span>
            </span>
            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
              <button onClick={() => openEdit(p)} style={iconBtn} title="Editar">
                <Pencil size={13} />
              </button>
              <button onClick={() => toggleActive(p)} style={iconBtn} title={p.is_active ? 'Desactivar' : 'Activar'}>
                {p.is_active ? <UserX size={13} style={{ color: '#ef4444' }} /> : <UserCheck size={13} style={{ color: '#16a34a' }} />}
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No se encontraron empleados.
          </div>
        )}
      </div>

      <PaginationBar
        page={page} pageSize={pageSize} totalPages={totalPages} totalItems={totalItems}
        onPageChange={setPage} onPageSizeChange={setPageSize}
      />

      {/* Modal */}
      {modalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setModalOpen(false)}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '24px 28px',
            width: '90%', maxWidth: 420, boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>
              {editing ? 'Editar Empleado' : 'Nuevo Empleado'}
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3, display: 'block' }}>ID</label>
                <input value={form.external_id} onChange={e => setForm({ ...form, external_id: e.target.value })}
                  style={fieldStyle} placeholder="Ej: 001" />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3, display: 'block' }}>Nombre</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  style={fieldStyle} placeholder="Nombre completo" />
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3, display: 'block' }}>Rol de Empresa</label>
              <select
                value={form.company_role || ''}
                onChange={e => setForm({ ...form, company_role: e.target.value || null })}
                style={fieldStyle}
              >
                {COMPANY_ROLES.map(r => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3, display: 'block' }}>
                  Clasificación de PTO
                </label>
                <select
                  value={form.pto_classification}
                  onChange={e => setForm({ ...form, pto_classification: e.target.value })}
                  style={fieldStyle}
                >
                  {PTO_CLASS_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, lineHeight: 1.35 }}>
                  Exento: vacaciones sin tope, enfermedad 120 h. No Exento: 192 h y 96 h.
                  Sin acumulación: fuera del régimen (subcontratados) — no acumula nada.
                  {' '}<strong>Sin clasificar: no acumula</strong> hasta que se le asigne una clasificación.
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3, display: 'block' }}>
                  Fecha de ingreso
                </label>
                <input
                  type="date"
                  value={form.hire_date}
                  onChange={e => setForm({ ...form, hire_date: e.target.value })}
                  style={fieldStyle}
                />
                {/* ⚠ Tarifa por hora — SOLO en el detalle, NUNCA como columna del listado.
                    Es compensación: la tabla la vería cualquiera que abra la pantalla, y de
                    un vistazo. PersonasPage ya es admin-only, así que el nivel de acceso es
                    el correcto (Lisette y la contable lo tienen) sin gate nuevo — lo que hay
                    que respetar es no sacarla de acá. */}
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b',
                                margin: '10px 0 3px', display: 'block' }}>
                  Tarifa por hora
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                                 fontSize: 13, color: '#94a3b8', pointerEvents: 'none' }}>$</span>
                  <input
                    type="number" step="0.01" min="0" inputMode="decimal"
                    value={form.hourly_rate}
                    onChange={e => setForm({ ...form, hourly_rate: e.target.value })}
                    placeholder="—"
                    style={{ ...fieldStyle, paddingLeft: 22 }}
                  />
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, lineHeight: 1.35 }}>
                  Vacío = sin tarifa registrada, que no es lo mismo que 0.
                </div>
              </div>
            </div>

            {/* V69 — Socio que aprueba las ausencias ADMINISTRATIVAS de esta persona.
                Ancho completo y fuera de la grilla de dos columnas: los nombres de los
                socios no entran en media fila.
                ⚠ Es a quién FIRMA, no un dato del empleado: la lista sale de `user_roles`
                (cuentas del portal), nunca de `personas.company_role = 'Socios'`. */}
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b',
                            margin: '10px 0 3px', display: 'block' }}>
              Socio que aprueba sus ausencias
            </label>
            <select
              value={form.pto_socio_user_id}
              onChange={e => setForm({ ...form, pto_socio_user_id: e.target.value })}
              style={fieldStyle}
            >
              <option value="">— Sin asignar —</option>
              {socios.map(s => (
                <option key={s.id} value={String(s.id)}>{s.name}</option>
              ))}
            </select>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, marginBottom: 10, lineHeight: 1.35 }}>
              Solo se usa en proyectos administrativos, donde no hay cuadrilla de donde
              sacar la cadena. Sin asignar, sus ausencias administrativas no se pueden
              registrar.
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 13, color: NAVY, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!form.wioa}
                onChange={e => setForm({ ...form, wioa: e.target.checked })}
              />
              Empleado WIOA
            </label>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              {editing && (
                <button onClick={() => { toggleActive(editing); setModalOpen(false); }} style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 6, padding: '6px 12px', fontSize: 11,
                  color: editing.is_active ? '#ef4444' : '#16a34a',
                  cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}>
                  {editing.is_active ? 'Desactivar' : 'Activar'}
                </button>
              )}
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                <button onClick={() => setModalOpen(false)} style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 20, padding: '6px 16px', fontSize: 12,
                  color: '#64748b', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}>
                  Cancelar
                </button>
                <button onClick={handleSave} disabled={saving} style={{
                  ...navyBtn, padding: '6px 16px', fontSize: 12,
                  opacity: saving ? 0.6 : 1,
                }}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BulkUploadModal
        open={showBulk}
        onClose={() => setShowBulk(false)}
        onSuccess={refreshPersonas}
        title="Carga Masiva de Empleados"
        endpoint="/api/admin/personas/bulk"
        templateColumns={['external_id', 'name', 'company_role', 'sick_days', 'vacation_days']}
        templateExample={[
          ['EMP-001', 'Juan Pérez', 'supervisor', '5', '15'],
          ['EMP-002', 'María García', 'ingeniero', '5', '15'],
        ]}
      />
    </div>
  );
}

const iconBtn = {
  background: BG_FIELD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
  padding: '6px 8px', cursor: 'pointer', color: NAVY, display: 'inline-flex', alignItems: 'center',
};
